/**
 * Stable deep-equality for form state comparisons.
 *
 * Designed to avoid false-positive "dirty" detections produced by naive
 * `JSON.stringify` comparison, which is sensitive to:
 *   - object key insertion order
 *   - the difference between `undefined`, missing keys, `null`, and `""`
 *   - "empty" map entries (e.g. PPPC choices set to `""` / `undefined`)
 *   - incidental leading/trailing whitespace in form strings (e.g. a trailing
 *     space accidentally typed in the PPPC Code Requirement input)
 *
 * Rules:
 *   - `undefined`, `null`, `""`, and any whitespace-only string are all
 *     considered equivalent ("empty")
 *   - On plain objects, keys whose values are "empty" are ignored on both sides
 *   - String leaves are compared after trimming leading/trailing whitespace,
 *     so `"abc"` and `"abc "` are equal. Internal whitespace remains significant.
 *   - Arrays compare element-wise in order
 *   - Numbers and booleans compare by value (NaN equals NaN)
 *   - Other types (Date, Map, Set, functions, class instances) fall back to
 *     reference equality — the form values we guard never use them
 */
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" &&
  v !== null &&
  !Array.isArray(v) &&
  (Object.getPrototypeOf(v) === Object.prototype ||
    Object.getPrototypeOf(v) === null);

const isEmpty = (v: unknown): boolean =>
  v === undefined ||
  v === null ||
  (typeof v === "string" && v.trim() === "");

export function stableDeepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;

  // Treat null/undefined/""/whitespace-only as interchangeable
  if (isEmpty(a) && isEmpty(b)) return true;
  if (isEmpty(a) !== isEmpty(b)) return false;

  // Whitespace-tolerant string comparison: trim leading/trailing whitespace
  // before comparing string leaves so that an accidental trailing space in a
  // form input (e.g. PPPC Code Requirement) doesn't trip the dirty flag.
  if (typeof a === "string" && typeof b === "string") {
    return a.trim() === b.trim();
  }

  if (typeof a !== typeof b) return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!stableDeepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    // Build a key set ignoring keys whose values are empty on both sides.
    const keys = new Set<string>();
    for (const k of Object.keys(a)) if (!isEmpty(a[k])) keys.add(k);
    for (const k of Object.keys(b)) if (!isEmpty(b[k])) keys.add(k);
    for (const k of keys) {
      if (!stableDeepEqual(a[k], b[k])) return false;
    }
    return true;
  }

  return false;
}

